import React from 'react'
import Graph1 from './Canvas/Graph1'
import EnergyProduction from './Canvas/EnergyProduction'
import './article.css'
import Header from './Header'
import DelayModel from './Canvas/DelayModel'
import DelayExamples from './Canvas/DelayExamples'
import LCOEPlot from './Canvas/LCOEPlot'
import bannerImage from './assets/Wallpaper.jpg'

const Post = () => {
  return (
    <div>
      <Header title="The Cost of Nuclear Energy" image={bannerImage} />
      <article>
        <header>
          <p>By <strong>Milo Peterson</strong> | <time dateTime="2025-06-2">July 20, 2025</time></p>
        </header>

        <section>
          <h2>Energy Challenges and Solutions</h2>
          <p>
            With concern about climate change and global warming on the rise, many innovators have been working hard on solutions.
            From CO₂ reclamation to massive ocean cleanup projects, a lot is being done by a few very dedicated people. The problem is, 
            cleaning up the planet is expensive—and both companies and individuals often can’t, or won’t, stop the practices that keep making things worse.
          </p>
          <p>
            Green energy doesn’t quite have that problem. Solar, wind, and hydroelectric power have created huge—and profitable—industrial sectors
            that make companies millions. Even factoring in government subsidies, renewables have big advantages. They’re renewable (obviously), 
            which means much lower operating costs compared to natural gas or coal. LCOE estimates vary, but onshore wind and solar PV 
            typically come in around $0.05/kWh and $0.07/kWh respectively—comparable to natural gas, which with the right tech can also hit about $0.05/kWh.
          </p>
          <p>
            Basically, green energy is cost-competitive. That’s not to say renewables don’t have downsides—otherwise, we’d already be running mostly on them.
            For wind and solar, the cost and efficiency depend heavily on location. Land costs a premium in sunny places like California, 
            and regions like the Middle East or the Sahara are too dusty for solar to work efficiently. 
            And if you’re trying to power a factory, solar is pretty much out unless you only plan to run during peak daylight hours or have a massive hydroplant to store energy.
          </p>
          <p>
            As a fuel-efficient and abundant source of stable, clean energy, nuclear power can make up for some of the shortcomings of renewables 
            and help provide a consistent baseline supply. So where are we right now? It really sounds like we have the means—and should have the motive—right?
          </p>
          <h3 style={{ textAlign: 'center' }}>Global Energy Production</h3>
          <EnergyProduction className="graph" />
          <p>
            As of five years ago, renewables and nuclear made up just about 17% of U.S. energy production. For renewables, the issue isn’t lack of growth.
            Renewables have seen pretty steady year-over-year increases, and since this chart was published, there’ve been a number of new projects 
            that put renewables alone around 20% of U.S. power generation by some estimates.
          </p>
          <p>
            Nuclear power has been the exception. Since the ’90s, the U.S. hasn’t just stagnated—it’s seen a noticeable decline in nuclear production.
            Compared to every other major energy source besides coal, nuclear is doing the worst.
          </p>
          <p>
            This isn’t just a U.S. problem either. With a few notable exceptions, nuclear energy has stagnated—or declined—around the world.
          </p>
          <h3 style={{ textAlign: 'center' }}>Nuclear Power as % of Total</h3>
          <Graph1 />
          <p>
            So why hasn’t nuclear energy grown, despite being green, consistent, and abundant? It sounds like exactly what we should want.
          </p>
          <p>
            Simply put, nuclear plants are too expensive. While they show promise similar to other clean energy sources, 
            they come with massive capital costs, long construction times, and huge financial risks.
          </p>
          <p>
            People blame a lot of factors for nuclear power’s sky-high costs. To narrow them down and figure out what could actually help bring prices down, 
            I’m going to break down the whole industry piece by piece.
          </p>
        </section>

        <section>
          <h2>Death by Legal Procedures</h2>
          <p>
            To understand how lawsuits and bureaucracy can choke an industry to death, it’s important to understand one of the biggest drivers of cost overruns: delays.
          </p>
          <DelayModel />
          <p>
            For a smaller $6B reactor, a 4-year delay can add half the original cost back on. And for larger projects? The drop-off gets way steeper.
            A $14B reactor—the original cost of Georgia’s new Vogtle power plant—can almost double in price after seven years. 
            If it’s delayed for ten years, as Vogtle was, the cost can balloon to 2.5 times the original budget.
          </p>
          <p>
            If delays were rare, this wouldn’t be a problem. Unfortunately, a nuclear project getting delayed is about as predictable 
            as Disney making another mediocre live-action remake—it just keeps happening. 
            The difference is, Disney’s mistakes cost you two hours of your life, not billions of dollars.
          </p>
          <DelayExamples />
          <p>
            If the only non-SMR reactor built in the U.S. since the ’80s cost more than double what it was supposed to and took an extra decade to finish, 
            it’s hard to justify “just trying again.”
          </p>
          <p>
            So, if I wanted to kill the nuclear industry, I’d simply tie up every new construction project in lawsuits. 
            By the time the legal dust settled, they’d have spent another $10B. Eventually, those overruns would make new projects unsustainable.
          </p>
          <p>
            Realistically, the U.S. legal system isn’t that easy to game—and these contractors aren’t exactly broke. 
            If they’ve already spent billions building reactors, you can bet they’ll spend a few more on lawyers. 
            Still, the point stands: delays and cost overruns plague the nuclear industry and continue to block it from becoming a truly cost-effective power source. 
            In another post, I’ll dig deeper into how I’d personally go about “ruining” the nuclear industry.
          </p>
        </section>

        <section>
          <h2>We Can Do Better</h2>
          <p>
            But what should nuclear power actually cost? Before all these delays, things were looking pretty good. 
            Let’s say a basic 1 GW reactor costs around $4B to build. After factoring in interest and taxes, 
            that works out to roughly $0.05/kWh.
          </p>
          <p>
            Add on the <a href="https://www.eia.gov/electricity/annual/html/epa_08_04.html">EIA’s OPEX estimates</a> of $0.022/kWh, 
            and you end up at about $0.072/kWh total.
          </p>
          <p>
            Price-wise, that puts nuclear on the same level as onshore wind and solar PV, according to 
            <a href="https://www.lazard.com/media/uounhon4/lazards-lcoeplus-june-2025.pdf">Lazard’s 2025 LCOE report</a>.
          </p>
          <LCOEPlot className="graph" />
          <p>
            In an ideal world, nuclear would actually be cheaper than any other form of energy. Of course, 
            that’s not the world we live in—but how can we get closer to that $4B capex number instead of the $20B that’s become normal?
          </p>
        </section>

        <section>
          <h2>Lowering the Price</h2>
          <p>
            There are other ideas—and with SMRs gaining popularity in the last few years, the future looks at least a little brighter 
            (maybe lava-lamp bright, but still). In my opinion, though, one change would make a huge difference: accountability.
          </p>
          <p>
            When an energy company starts a nuclear project—like Georgia Power with Vogtle—they expect a set profit margin, 
            maybe 10% or 20%. Because they operate under a loose regulatory model, they’re guaranteed a return on investment. 
            That includes Vogtle, meaning all cost overruns and deficits get pushed back onto customers. 
            So, because Vogtle failed so badly, Georgia Power customers are already <a href="https://www.georgia-ces.org/issues">facing a 29% increase</a> in their average electric bill.
            Georgia Power itself faces little to no repercussions for the mess.
          </p>
          <p>
            These companies push all the business risk onto their customers so their ROE can stay 50% above market rate. 
            Since nothing changes, they have no incentive to improve construction efficiency—and instead just charge customers more to cover their losses.
          </p>
          <p>
            Whether you’re a fan of nuclear power or not, corporations shouldn’t be able to pass all their risk onto consumers. 
            There need to be regulatory changes that force these companies to actually care about cost control.
          </p>
        </section>

        <section>
          <h2>Reevaluation</h2>
          <p>Let’s take a step back and reevaluate nuclear power.</p>
          <p>
            We’ve gone over the massive cost and motivation problems haunting the nuclear industry. Are they chronic? Can they be fixed? 
            I think the answer is a clear yes. But figuring out how to change the industry will take more thought and a deeper look into its many moving parts.
            It’s not perfect—but there are real solutions. I’ll be writing more posts diving into them specifically.
            In the meantime, it’s worth remembering that while we’ve made real progress on climate change and renewables, 
            we’re still nowhere near fully green. To get there, it’d be a mistake to ignore nuclear’s massive potential.
          </p>
        </section>

        <section>
          <h2>References</h2>
          <ul style={{ listStyleType: "none", paddingLeft: 0 }}>
            <li>
              Georgia Conservation Voters Education Fund. (2021). <i>Vogtle ratepayer robbery report</i>.&nbsp;
              <a href="https://gcvedfund.org/wp-content/uploads/sites/3/2021/12/GCV-Vogtle-Ratepayer-Robbery-Report-2.pdf" target="_blank" rel="noopener noreferrer">
                https://gcvedfund.org/wp-content/uploads/sites/3/2021/12/GCV-Vogtle-Ratepayer-Robbery-Report-2.pdf
              </a>
            </li>
            <br />
            <li>
              U.S. Energy Information Administration. (n.d.). <i>U.S. Energy Information Administration (EIA)</i>. Retrieved August 17, 2025, from&nbsp;
              <a href="https://www.eia.gov" target="_blank" rel="noopener noreferrer">
                https://www.eia.gov
              </a>
            </li>
            <br />
            <li>
              World Nuclear Association. (2023). <i>Economics of nuclear power</i>.&nbsp;
              <a href="https://world-nuclear.org/information-library/economic-aspects/economics-of-nuclear-power#economic-implications-of-particular-plants" target="_blank" rel="noopener noreferrer">
                https://world-nuclear.org/information-library/economic-aspects/economics-of-nuclear-power#economic-implications-of-particular-plants
              </a>
            </li>
            <br />
            <li>
              U.S. Energy Information Administration. (2011, August 30). <i>Average wholesale electric power prices increased across the country in 2010</i>. Today in Energy.&nbsp;
              <a href="https://www.eia.gov/todayinenergy/detail.php?id=370" target="_blank" rel="noopener noreferrer">
                https://www.eia.gov/todayinenergy/detail.php?id=370
              </a>
            </li>
            <br />
            <li>
              World Bank. (n.d.). <i>World Bank</i>. Retrieved August 17, 2025, from &nbsp;
              <a href="https://www.worldbank.org" target="_blank" rel="noopener noreferrer">
                https://www.worldbank.org
              </a>
            </li>
          </ul>
        </section>

        <footer>
          
        </footer>
      </article>
    </div>
  )
}

export default Post
